/**
 * User: Profile
 * @see {@link ../components/Common/UserProfileForm.jsx}
 */
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Box,
  Button,
  Alert,
  CircularProgress,
  Paper,
  Container,
} from "@mui/material";
import { Save as SaveIcon } from "@mui/icons-material";
import "react-phone-number-input/style.css";
import { isValidPhoneNumber } from "react-phone-number-input";
import { isEmail } from "../utils/helper";

import PageLayout from "../components/Common/PageLayout";
import PageBanner from "../components/Common/PageBanner";
// Import shared auth client and actions
import { getProfile, updateProfile } from "@smfa/utils/src";
import {
  ethnicityOptions,
  genderOptions,
  pronounOptions,
  raceOptions,
  sexualOrientationOptions,
} from "@smfa/utils/src/data/demographic-options";

import UserProfileForm, {
  requiredFields as baseRequiredFields,
} from "../components/Common/UserProfileForm";
import { forEach, get } from "lodash";

/**
 * User profile
 * @returns {JSX.Element}
 * @component
 */
const Profile = () => {
  // Required form fields for UI indication and data validation
  const requiredFields = [...baseRequiredFields, "country"];

  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(
    useSelector((state) => state.pageContents?.loading),
  ); // Loading state from Redux
  const auth = useSelector((state) => state.auth) || {};
  const user = useSelector((state) => state.user.data);
  const isAuthenticated = Boolean(auth?.isAuthenticated);

  // State variables
  const [saving, setSaving] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [profileData, setProfileData] = useState(null);
  const [formData, setFormData] = useState({});
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});

  // Check if user is authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      log.warn("Not authenticated - redirecting to login");
      navigate("/login?redirect=/profile");
    }
  }, [isAuthenticated, navigate]);

  // Fetch profile data on component mount
  useEffect(() => {
    if (isAuthenticated) {
      fetchProfileData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  // Pre-populate form with user data from Redux
  useEffect(() => {
    // Map of form fields to user data
    const formMap = {
      // form name => state.user.data.* path
      email_id: "email",
      first_name: "profile.first_name",
      last_name: "profile.last_name",
      phone_number: "profile.phone_number",
      institution: "profile.institution",
      country: "profile.country",
      city: "profile.city",
      state: "profile.state",
      degree: "profile.degree",
    };
    if (user?.data) {
      const newformData = { ...formData };
      let hasAssignments = false;
      forEach(formMap, (value, key) => {
        const userVal = get(user?.data, value, false);
        if (userVal) {
          newformData[key] = userVal;
          hasAssignments = true;
        }
      });

      if (hasAssignments) {
        setProfileData((prev) => ({ ...prev, ...newformData }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Fetch profile data from API
  const fetchProfileData = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await getProfile();
      log.debug("Profile API response:", response.data); // Debug the response

      if (response.data.status) {
        // Extract the profile data which is nested inside data.profile
        const profileData = response.data.data.profile || {};

        // Format phone number to E.164 if it exists but isn't properly formatted
        let formattedPhoneNumber = profileData.phone_number || "";
        if (formattedPhoneNumber && !formattedPhoneNumber.startsWith("+")) {
          try {
            // Try to format as US number if no country code
            // If it's already a valid E.164 number, this won't do anything
            formattedPhoneNumber = formattedPhoneNumber.replace(/[^\d]/g, "");
            if (formattedPhoneNumber.length > 6) {
              // Only attempt formatting if it looks like a real number
              formattedPhoneNumber = `+1${formattedPhoneNumber}`; // Assume US (+1)
            }
          } catch (phoneErr) {
            log.warn("Could not format phone number:", phoneErr);
            formattedPhoneNumber = ""; // Reset to empty on parse failure
          }
        }

        // Map non-standard values to our dropdown options or set to empty string
        const mapFieldToValidOption = (field, options) => {
          // If the field value is in our options, keep it
          if (!field || options.some((opt) => opt.value === field)) {
            return field;
          }
          // Otherwise set to empty string
          log.warn(
            `Invalid option value for ${field}, defaulting to empty string`,
          );
          return "";
        };

        // Process languages field - ensure it's an array
        let languagesArray = [];
        if (profileData.language) {
          if (typeof profileData.language === "string") {
            // Handle CSV format string
            languagesArray = profileData.language
              .split(",")
              .map((lang) => lang.trim());
          } else if (Array.isArray(profileData.language)) {
            // It's already an array
            languagesArray = profileData.language;
          }
          // Log for debugging
          log.debug("Processed languages:", languagesArray);
        }

        // The backend should now provide all fields, but we'll still handle any missing ones
        const enhancedProfileData = {
          ...profileData,
          phone_number: formattedPhoneNumber, // Use the formatted phone number
          // Map any invalid values to valid options
          gender: mapFieldToValidOption(profileData.gender, genderOptions),
          race: mapFieldToValidOption(profileData.race, raceOptions),
          ethnicity: mapFieldToValidOption(
            profileData.ethnicity,
            ethnicityOptions,
          ),
          pronouns: mapFieldToValidOption(profileData.pronouns, pronounOptions),
          sexual_orientation: mapFieldToValidOption(
            profileData.sexual_orientation,
            sexualOrientationOptions,
          ),
          // Ensure language is always an array
          language: languagesArray,
          // Keep other fields as is
          email_id: profileData.email_id || response.data.data.email || "",
          middle_name: profileData.middle_name || "",
        };

        log.debug("Processed profile data:", enhancedProfileData);

        setProfileData(enhancedProfileData);
        setFormData(enhancedProfileData);
      } else {
        setError(response.data.message || "Failed to load profile data");
      }
    } catch (err) {
      log.error("Error fetching profile data:", err);

      // Log the complete error response for debugging
      log.error("Error details:", {
        status: err.response?.status,
        data: err.response?.data,
        headers: err.response?.headers,
      });

      setError(
        err.response?.data?.message ||
          "An error occurred while fetching your profile data",
      );

      // Handle unauthorized specifically - redirect to login
      if (err.response?.status === 401) {
        setTimeout(() => {
          navigate("/login?redirect=/profile&reason=session_expired");
        }, 2000);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear validation error for this field
    if (validationErrors[name]) {
      setValidationErrors((prev) => ({
        ...prev,
        [name]: null,
      }));
    }
  };

  // Handle date change for date picker
  const handleDateChange = (date) => {
    setFormData((prev) => ({
      ...prev,
      dob: date ? date.format("YYYY-MM-DD") : null,
    }));

    // Clear validation error for this field
    if (validationErrors.dob) {
      setValidationErrors((prev) => ({
        ...prev,
        dob: null,
      }));
    }
  };

  // Handle phone number change specifically
  const handlePhoneChange = (value) => {
    // PhoneInput component will provide a properly formatted E.164 value or undefined
    setFormData((prev) => ({
      ...prev,
      phone_number: value || "",
    }));

    // Clear validation error for phone field
    if (validationErrors.phone_number) {
      setValidationErrors((prev) => ({
        ...prev,
        phone_number: null,
      }));
    }
  };

  // Specialized handlers for country and languages
  const handleCountryChange = (value) => {
    setFormData((prev) => ({ ...prev, country: value }));
  };

  const handleLanguageChange = (values) => {
    // Ensure values is treated as array
    const languagesArray = Array.isArray(values)
      ? values
      : [values].filter(Boolean);
    log.debug("Language change:", languagesArray);
    setFormData((prev) => ({ ...prev, language: languagesArray }));
  };

  // Validate form data
  const validateForm = () => {
    let errors = {};

    requiredFields.forEach((field) => {
      if (!formData[field]?.trim()) {
        errors[field] = "This field is required";
      }
    });

    if (!isEmail(formData.email_id)) {
      errors.email_id = "Invalid email address";
    }

    // Phone validation - only if a value exists (not required)
    if (formData.phone_number) {
      try {
        // Only validate if there's a value to validate
        if (!isValidPhoneNumber(formData.phone_number)) {
          errors.phone_number = "Phone number is invalid";
        }
      } catch (e) {
        errors.phone_number = "Phone number format is invalid";
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Save profile changes using shared client
  const saveChanges = async () => {
    if (!validateForm()) {
      return;
    }

    setSaving(true);
    setError(null);
    setSuccess(null);

    // Strip undefined or null values from formData to avoid issues with the API
    const cleanedFormData = Object.fromEntries(
      Object.entries(formData).filter(
        ([_, value]) => value !== null && value !== undefined,
      ),
    );

    // Convert languages array to comma-separated string for API
    if (cleanedFormData.language && Array.isArray(cleanedFormData.language)) {
      cleanedFormData.language = cleanedFormData.language.join(",");
    }

    log.debug("Saving profile data:", cleanedFormData);

    try {
      const response = await updateProfile(cleanedFormData);

      if (response.data.status) {
        setSuccess("Profile updated successfully");

        // Make sure to get the profile data from the correct location
        const updatedProfileData = response.data.data.profile || {};
        setProfileData(updatedProfileData);

        // If email was changed, we should redirect to login page
        if (formData.email_id !== user.email) {
          setTimeout(() => {
            // Force logout and redirect to login
            log.warn("Login redirect due to email change");
            window.location.href = "/login";
          }, 2000);
        }
      } else {
        setError(response.data.message || "Failed to update profile");
      }
    } catch (err) {
      log.error("Error updating profile:", err);
      setError(
        err.response?.data?.message ||
          "An error occurred while updating your profile",
      );
    } finally {
      setSaving(false);
    }
  };

  if (!isAuthenticated) {
    return null; // Don't render anything if not authenticated
  }

  return (
    <PageLayout pageName="Member Profile" checkComingSoon={false}>
      <PageBanner dense mainTitle="Member Profile" pageTitle="Profile" />

      <Container maxWidth="lg">
        <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
          {isLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          ) : (
            <>
              {success && (
                <Alert severity="success" sx={{ mb: 3 }}>
                  {success}
                </Alert>
              )}

              <form>
                <UserProfileForm
                  {...{
                    formData,
                    handleInputChange,
                    handleDateChange,
                    handlePhoneChange,
                    handleCountryChange,
                    handleLanguageChange,
                    validationErrors,
                    append: (
                      <Box
                        sx={{
                          mt: 4,
                          display: "flex",
                          justifyContent: "flex-end",
                        }}
                      >
                        <Button
                          variant="contained"
                          color="primary"
                          startIcon={
                            saving ? (
                              <CircularProgress size={20} color="inherit" />
                            ) : (
                              <SaveIcon />
                            )
                          }
                          onClick={saveChanges}
                          disabled={saving}
                        >
                          {saving ? "Saving..." : "Save Changes"}
                        </Button>
                      </Box>
                    ),
                    required: requiredFields,
                  }}
                />
              </form>
            </>
          )}
        </Paper>
      </Container>
    </PageLayout>
  );
};

export default Profile;
